import axios, { AxiosInstance, AxiosError } from 'axios';

export enum IdType {
  PersonalId = 0,
  CompanyId = 1,
  DIMEX = 2,
  NITE = 3
}

export interface RegisterAccountDto {
  email: string;
  nationalId: string;
  idType: IdType;
  countryCode: string;
  preferredLanguage: string;
  recaptchaToken?: string;
}



export interface AccountDto {
  id: string;
  email: string;
  fullName?: string;
  notificationsEnabled: boolean;
  emailConfirmed: boolean;
  countryCode: string;
  preferredLanguage: string;
  createdAt: string;
  monitoredIdentities: MonitoredIdentityDto[];
}

export interface MonitoredIdentityDto {
  id: string;
  accountId?: string;
  nationalIdId: string;
  label?: string;
  fullName?: string; // For companies only
  isActive: boolean;
  createdAt: string;
  nationalId: NationalIdDto;
  existingMentionCount?: number;
}

export interface NationalIdDto {
  id: string;
  countryCode: string;
  idType: IdType;
  normalizedId: string;
  firstName?: string;
  middleNames?: string;
  lastName1?: string;
  lastName2?: string;
  fullName?: string;
  createdAt: string;
}

export interface UpdateAccountDto {
  fullName?: string;
  notificationsEnabled: boolean;
  preferredLanguage: string;
}

export interface CreateMonitoredIdentityDto {
  nationalId: string;
  idType: IdType;
}

export interface UpdateMonitoredIdentityDto {
  label?: string;
  fullName?: string; // For companies only
  isActive: boolean;
}

export interface UpdateLabelDto {
  label?: string;
}

export enum MentionMatchType {
  ExactId = 0,
  NameHeuristic = 1
}

export enum MentionFeedbackDecision {
  Accepted = 0,
  Rejected = 1
}

export interface PublicationDto {
  id: number;
  title: string;
  source: string;
  publicationDate: string;
  referenceId: string;
  sourceUrl?: string;
  summary?: string;
  documentSizeBytes?: number;
  countryCode: string;
  createdAt: string;
}

export interface MentionDto {
  id: number;
  nationalIdId: string;
  publicationId: number;
  matchType: MentionMatchType;
  matchedText: string;
  context?: string;
  pageNumber?: number;
  notificationSent: boolean;
  notificationSentAt?: string;
  createdAt: string;
  publication: PublicationDto;
  nationalId: NationalIdDto;
  feedbackSummary?: MentionFeedbackSummaryDto;
  userFeedback?: MentionFeedbackDto;
}

export interface MentionFeedbackDto {
  id: string;
  mentionId: number;
  userId: number;
  decision: MentionFeedbackDecision;
  timestamp: string;
}

export interface CreateMentionFeedbackDto {
  decision: MentionFeedbackDecision;
}

export interface MentionFeedbackSummaryDto {
  totalFeedbackCount: number;
  acceptedFeedbackCount: number;
  rejectedFeedbackCount: number;
  dominantFeedback: MentionFeedbackDecision;
}

export interface PaginatedResponse<T> {
  data: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface AuthLoginDto {
  email: string;
}

export interface AuthLoginResponseDto {
  message: string;
}

export interface LoginResponseDto {
  message: string;
  success: boolean;
  details?: Record<string, any>;
}

export interface AuthVerifyDto {
  token: string;
}

export interface AuthVerifyResponseDto {
  token: string;
  account: AccountDto;
  expiresAt: string;
  landingUrl?: string;
  additionalParams?: Record<string, string>;
}

export interface EmailConfirmationResponseDto {
  token: string;
  account: AccountDto;
  expiresAt: string;
  success: boolean;
  message: string;
}

// Error response interfaces matching backend DTOs
export interface ErrorResponseDto {
  message: string;
  errorCode?: string;
  details?: Record<string, any>;
  timestamp: string;
}

export interface ValidationErrorResponseDto extends ErrorResponseDto {
  errors: Record<string, string[]>;
}

export interface NameResolutionErrorResponseDto extends ErrorResponseDto {
  operationCompleted: boolean;
  nameResolutionStatus?: NameResolutionStatusDto;
}

export interface NameResolutionStatusDto {
  attempted: boolean;
  successful: boolean;
  failureReason?: string;
  isRetryable: boolean;
  fallbackValue?: string;
}

export class ApiError extends Error {
  public errorCode?: string;
  public details?: Record<string, any>;
  public validationErrors?: Record<string, string[]>;
  public nameResolutionStatus?: NameResolutionStatusDto;

  constructor(
    message: string, 
    public statusCode: number,
    errorResponse?: ErrorResponseDto | ValidationErrorResponseDto | NameResolutionErrorResponseDto
  ) {
    super(message);
    this.name = 'ApiError';
    
    if (errorResponse) {
      this.errorCode = errorResponse.errorCode;
      this.details = errorResponse.details;
      
      // Handle validation errors
      if ('errors' in errorResponse) {
        this.validationErrors = errorResponse.errors;
      }
      
      // Handle name resolution errors
      if ('nameResolutionStatus' in errorResponse) {
        this.nameResolutionStatus = errorResponse.nameResolutionStatus;
      }
    }
  }

  /**
   * Check if this is a specific error code
   */
  isErrorCode(code: string): boolean {
    return this.errorCode === code;
  }

  /**
   * Check if this is a validation error
   */
  isValidationError(): boolean {
    return this.validationErrors !== undefined;
  }

  /**
   * Check if this is a name resolution error
   */
  isNameResolutionError(): boolean {
    return this.nameResolutionStatus !== undefined;
  }

  /**
   * Get validation errors for a specific field
   */
  getFieldErrors(fieldName: string): string[] {
    return this.validationErrors?.[fieldName] || [];
  }
}

class ApiService {
  private readonly client: AxiosInstance;

  constructor() {
    const baseURL = import.meta.env.PROD 
      ? import.meta.env.VITE_API_URL 
      : '/api';

    this.client = axios.create({
      baseURL,
      timeout: 35000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        let message = 'Network error. Please try again.';
        let errorResponse: ErrorResponseDto | ValidationErrorResponseDto | NameResolutionErrorResponseDto | undefined;
        
        if (error.response?.data) {
          if (typeof error.response.data === 'string') {
            message = error.response.data;
          } else if (typeof error.response.data === 'object' && error.response.data !== null) {
            const errorData = error.response.data as any;
            message = errorData.message || errorData.error || message;
            
            // Try to parse as structured error response
            if (errorData.message && errorData.timestamp) {
              errorResponse = errorData as ErrorResponseDto;
            }
          }
        } else if (error.message) {
          message = error.message;
        }
        
        // Check for timeout errors specifically
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          message = 'timeout_error'; // Special marker for timeout errors
        }
        
        const statusCode = error.response?.status || 500;
        throw new ApiError(message, statusCode, errorResponse);
      }
    );
  }

  async registerAccount(userData: RegisterAccountDto): Promise<LoginResponseDto> {
    try {
      const response = await this.client.post<LoginResponseDto>('/accounts/register', userData);
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Registration failed', 500);
    }
  }




  async provideMentionFeedback(mentionId: number, feedback: CreateMentionFeedbackDto, token: string): Promise<MentionFeedbackDto> {
    try {
      const response = await this.client.post<MentionFeedbackDto>(`/mentions/${mentionId}/feedback`, feedback, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to provide feedback', 500);
    }
  }

  async getMentionFeedback(mentionId: number, token: string): Promise<MentionFeedbackDto | null> {
    try {
      const response = await this.client.get<MentionFeedbackDto>(`/mentions/${mentionId}/feedback`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 404) {
        return null; // No feedback exists yet
      }
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to fetch feedback', 500);
    }
  }

  async getMentionsFeedbackHistory(token: string, page: number = 1, pageSize: number = 20): Promise<MentionFeedbackDto[]> {
    try {
      const response = await this.client.get<MentionFeedbackDto[]>(`/mentions/feedback/user?page=${page}&pageSize=${pageSize}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to fetch feedback history', 500);
    }
  }

  async deleteMentionFeedback(mentionId: number, token: string): Promise<void> {
    try {
      await this.client.delete(`/mentions/${mentionId}/feedback`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to delete feedback', 500);
    }
  }

  async login(email: string, recaptchaToken?: string): Promise<AuthLoginResponseDto> {
    try {
      const response = await this.client.post<AuthLoginResponseDto>('/auth/login', { 
        email,
        recaptchaToken 
      });
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Login failed', 500);
    }
  }

  async verifyToken(token: string, landingUrl?: string, additionalParams?: Record<string, string>): Promise<AuthVerifyResponseDto> {
    try {
      const payload: any = { token };
      if (landingUrl) {
        payload.landingUrl = landingUrl;
      }
      if (additionalParams) {
        payload.additionalParams = additionalParams;
      }
      
      const response = await this.client.post<AuthVerifyResponseDto>('/auth/verify', payload);
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Token verification failed', 500);
    }
  }

  async confirmEmail(token: string): Promise<EmailConfirmationResponseDto> {
    try {
      const response = await this.client.get<EmailConfirmationResponseDto>(`/accounts/confirm-email/${token}`);
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Email confirmation failed', 500);
    }
  }

  async unsubscribeUser(token: string): Promise<{ message: string }> {
    try {
      const response = await this.client.post<{ message: string }>(`/accounts/unsubscribe/${token}`);
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Unsubscribe failed', 500);
    }
  }

  async getAccountProfile(token: string): Promise<AccountDto> {
    try {
      const response = await this.client.get<AccountDto>('/accounts/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to load account profile', 500);
    }
  }

  async updateAccountProfile(token: string, accountData: UpdateAccountDto): Promise<void> {
    try {
      await this.client.put('/accounts/profile', accountData, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to update account profile', 500);
    }
  }

  async deleteAccount(token: string): Promise<void> {
    try {
      await this.client.delete('/accounts', {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to delete account', 500);
    }
  }

  async getMonitoredIdentities(token: string): Promise<MonitoredIdentityDto[]> {
    try {
      const response = await this.client.get<MonitoredIdentityDto[]>('/identities', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to load monitored identities', 500);
    }
  }

  async getMonitoredIdentity(token: string, id: string): Promise<MonitoredIdentityDto> {
    try {
      const response = await this.client.get<MonitoredIdentityDto>(`/identities/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to load monitored identity', 500);
    }
  }

  async createMonitoredIdentity(token: string, identityData: CreateMonitoredIdentityDto): Promise<MonitoredIdentityDto> {
    try {
      const response = await this.client.post<MonitoredIdentityDto>('/identities', identityData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to create monitored identity', 500);
    }
  }

  async updateMonitoredIdentity(token: string, id: string, identityData: UpdateMonitoredIdentityDto): Promise<MonitoredIdentityDto> {
    try {
      const response = await this.client.put<MonitoredIdentityDto>(`/identities/${id}`, identityData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to update monitored identity', 500);
    }
  }

  async updateMonitoredIdentityLabel(token: string, id: string, labelData: UpdateLabelDto): Promise<MonitoredIdentityDto> {
    try {
      const response = await this.client.patch<MonitoredIdentityDto>(`/identities/${id}/label`, labelData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to update monitored identity label', 500);
    }
  }

  async deleteMonitoredIdentity(token: string, id: string): Promise<void> {
    try {
      await this.client.delete(`/identities/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to delete monitored identity', 500);
    }
  }

  async getMonitoredIdentityMentions(token: string, id: string, page: number = 1, pageSize: number = 20): Promise<MentionDto[]> {
    try {
      const response = await this.client.get<PaginatedResponse<MentionDto>>(`/identities/${id}/mentions?page=${page}&pageSize=${pageSize}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.data; // Extract the data array from the paginated response
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to load mentions for monitored identity', 500);
    }
  }

  async getAllAccountMentions(token: string, page: number = 1, pageSize: number = 20): Promise<MentionDto[]> {
    try {
      const response = await this.client.get<PaginatedResponse<MentionDto>>(`/identities/mentions?page=${page}&pageSize=${pageSize}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.data; // Extract the data array from the paginated response
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to load all mentions', 500);
    }
  }

  async getAdminUsers(
    token: string,
    page: number = 1,
    pageSize: number = 50,
    sortBy: string = 'createdAt',
    sortDescending: boolean = true
  ): Promise<any> {
    try {
      const response = await this.client.get('/admin/users', {
        params: {
          page,
          pageSize,
          sortBy,
          sortDescending
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 403) {
          throw new ApiError('Admin privileges required to access user list', 403);
        } else if (error.statusCode === 401) {
          throw new ApiError('Authentication required. Please log in again.', 401);
        }
        throw error;
      }
      throw new ApiError('Failed to load users', 500);
    }
  }

  async getAdminUserById(userId: string, token: string): Promise<any> {
    try {
      const response = await this.client.get(`/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 403) {
          throw new ApiError('Admin privileges required to access user details', 403);
        } else if (error.statusCode === 401) {
          throw new ApiError('Authentication required. Please log in again.', 401);
        } else if (error.statusCode === 404) {
          throw new ApiError('User not found', 404);
        }
        throw error;
      }
      throw new ApiError('Failed to load user details', 500);
    }
  }

  async getAdminUserMonitoredIdentities(
    userId: string,
    page: number = 1,
    pageSize: number = 20,
    token: string
  ): Promise<any> {
    try {
      const response = await this.client.get(`/admin/users/${userId}/monitored-identities`, {
        params: { page, pageSize },
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 403) {
          throw new ApiError('Admin privileges required', 403);
        } else if (error.statusCode === 401) {
          throw new ApiError('Authentication required. Please log in again.', 401);
        } else if (error.statusCode === 404) {
          throw new ApiError('User not found', 404);
        }
        throw error;
      }
      throw new ApiError('Failed to load monitored identities', 500);
    }
  }

  async updateAdminUserStatus(userId: string, dto: any, token: string): Promise<any> {
    try {
      const response = await this.client.patch(`/admin/users/${userId}/status`, dto, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 403) {
          throw new ApiError('Admin privileges required', 403);
        } else if (error.statusCode === 401) {
          throw new ApiError('Authentication required. Please log in again.', 401);
        } else if (error.statusCode === 404) {
          throw new ApiError('User not found', 404);
        } else if (error.statusCode === 400) {
          throw new ApiError('Cannot perform this action on your own account', 400);
        }
        throw error;
      }
      throw new ApiError('Failed to update user status', 500);
    }
  }

  async updateAdminUserPrivileges(userId: string, dto: any, token: string): Promise<any> {
    try {
      const response = await this.client.patch(`/admin/users/${userId}/admin`, dto, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 403) {
          throw new ApiError('Admin privileges required', 403);
        } else if (error.statusCode === 401) {
          throw new ApiError('Authentication required. Please log in again.', 401);
        } else if (error.statusCode === 404) {
          throw new ApiError('User not found', 404);
        } else if (error.statusCode === 400) {
          throw new ApiError('Cannot revoke admin privileges from the last administrator', 400);
        }
        throw error;
      }
      throw new ApiError('Failed to update admin privileges', 500);
    }
  }

  // Email Queue Methods

  async getAdminEmailQueueStats(token: string): Promise<any> {
    try {
      const response = await this.client.get('/admin/email-queue/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 403) {
          throw new ApiError('Admin privileges required', 403);
        } else if (error.statusCode === 401) {
          throw new ApiError('Authentication required. Please log in again.', 401);
        }
        throw error;
      }
      throw new ApiError('Failed to fetch email queue statistics', 500);
    }
  }

  async getAdminEmailQueue(
    query: {
      pageNumber?: number;
      pageSize?: number;
      status?: string;
      emailType?: string;
      recipientEmail?: string;
      sortBy?: string;
      sortDescending?: boolean;
    },
    token: string
  ): Promise<any> {
    try {
      const response = await this.client.get('/admin/email-queue', {
        params: query,
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 403) {
          throw new ApiError('Admin privileges required', 403);
        } else if (error.statusCode === 401) {
          throw new ApiError('Authentication required. Please log in again.', 401);
        }
        throw error;
      }
      throw new ApiError('Failed to fetch email queue', 500);
    }
  }

  async getAdminEmailQueueById(id: number, token: string): Promise<any> {
    try {
      const response = await this.client.get(`/admin/email-queue/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 403) {
          throw new ApiError('Admin privileges required', 403);
        } else if (error.statusCode === 401) {
          throw new ApiError('Authentication required. Please log in again.', 401);
        } else if (error.statusCode === 404) {
          throw new ApiError('Email not found', 404);
        }
        throw error;
      }
      throw new ApiError(`Failed to fetch email ${id}`, 500);
    }
  }

  async retryAdminEmail(id: number, token: string): Promise<any> {
    try {
      const response = await this.client.post(`/admin/email-queue/${id}/retry`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 403) {
          throw new ApiError('Admin privileges required', 403);
        } else if (error.statusCode === 401) {
          throw new ApiError('Authentication required. Please log in again.', 401);
        } else if (error.statusCode === 404) {
          throw new ApiError('Email not found', 404);
        }
        throw error;
      }
      throw new ApiError(`Failed to retry email ${id}`, 500);
    }
  }

  async deleteAdminEmail(id: number, token: string): Promise<void> {
    try {
      await this.client.delete(`/admin/email-queue/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 403) {
          throw new ApiError('Admin privileges required', 403);
        } else if (error.statusCode === 401) {
          throw new ApiError('Authentication required. Please log in again.', 401);
        } else if (error.statusCode === 404) {
          throw new ApiError('Email not found', 404);
        }
        throw error;
      }
      throw new ApiError(`Failed to delete email ${id}`, 500);
    }
  }

  async triggerAdminEmailProcessing(token: string): Promise<void> {
    try {
      await this.client.post('/admin/email-queue/process', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 403) {
          throw new ApiError('Admin privileges required', 403);
        } else if (error.statusCode === 401) {
          throw new ApiError('Authentication required. Please log in again.', 401);
        }
        throw error;
      }
      throw new ApiError('Failed to trigger email queue processing', 500);
    }
  }

}

export const apiService = new ApiService();
export const api = apiService;